import React from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { Brand } from '../ui';
import { activeItem, visibleSections, NavItem } from './navigation';
import { useAuth } from '../../context/AuthContext';
import { layout, tint } from '../../theme';

interface SidebarProps {
  /** Expanded vs. icon rail on desktop; open vs. closed on mobile. */
  open: boolean;
  mobile: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ open, mobile, onClose, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const groups = user?.groups ?? [];
  const sections = visibleSections(groups);
  const current = activeItem(location.pathname, groups);
  // The mobile drawer is always full width — a rail you cannot hover is useless.
  const expanded = mobile || open;

  const go = (item: NavItem) => {
    navigate(item.path);
    if (mobile) onClose();
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          height: layout.topbarHeight,
          px: expanded ? 2.5 : 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'flex-start' : 'center',
          flexShrink: 0,
        }}
      >
        <Brand compact={!expanded} />
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1.5 }}>
        {sections.map((section, index) => (
          <Box key={section.heading} sx={{ mb: 1 }}>
            {expanded ? (
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  px: 3,
                  pt: index === 0 ? 0.5 : 1.5,
                  pb: 0.75,
                  color: 'text.disabled',
                }}
              >
                {section.heading}
              </Typography>
            ) : (
              index > 0 && <Divider sx={{ mx: 2, my: 1.25 }} />
            )}

            <List disablePadding sx={{ px: expanded ? 1.5 : 1.25 }}>
              {section.items.map((item) => {
                const selected = current?.path === item.path;
                const button = (
                  <ListItemButton
                    selected={selected}
                    onClick={() => go(item)}
                    aria-current={selected ? 'page' : undefined}
                    sx={{
                      position: 'relative',
                      minHeight: 44,
                      mb: 0.25,
                      px: expanded ? 1.5 : 0,
                      justifyContent: expanded ? 'flex-start' : 'center',
                      color: selected ? 'primary.main' : 'text.secondary',
                      '&.Mui-selected': { bgcolor: 'action.selected' },
                      '&.Mui-selected:hover': {
                        bgcolor: (theme) => tint(theme, 'primary.main', 0.16),
                      },
                      '&:hover': { color: selected ? 'primary.main' : 'text.primary' },
                      // The accent rail marks the current page without relying
                      // on the tint alone, which is faint at low contrast.
                      '&::before': selected
                        ? {
                            content: '""',
                            position: 'absolute',
                            left: expanded ? -6 : -5,
                            top: 10,
                            bottom: 10,
                            width: 3,
                            borderRadius: 3,
                            bgcolor: 'primary.main',
                          }
                        : undefined,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: expanded ? 34 : 0, '& svg': { fontSize: 21 } }}>
                      {item.icon}
                    </ListItemIcon>
                    {expanded && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: selected ? 600 : 500,
                          noWrap: true,
                        }}
                      />
                    )}
                  </ListItemButton>
                );

                return expanded ? (
                  <React.Fragment key={item.path}>{button}</React.Fragment>
                ) : (
                  <Tooltip key={item.path} title={item.label} placement="right">
                    <span>{button}</span>
                  </Tooltip>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {!mobile && (
        <>
          <Divider />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={expanded ? 'space-between' : 'center'}
            sx={{ px: expanded ? 2.5 : 1, py: 1.25, flexShrink: 0 }}
          >
            {expanded && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                AgentX Manager
              </Typography>
            )}
            <Tooltip title={expanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right">
              <IconButton
                size="small"
                onClick={onToggleCollapse}
                aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                {expanded ? (
                  <KeyboardDoubleArrowLeftIcon fontSize="small" />
                ) : (
                  <KeyboardDoubleArrowRightIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </>
      )}
    </Box>
  );

  const width = expanded ? layout.sidebarWidth : layout.sidebarRailWidth;

  return (
    <Drawer
      variant={mobile ? 'temporary' : 'permanent'}
      open={mobile ? open : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: mobile ? 0 : width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter,
            }),
        },
      }}
    >
      {content}
    </Drawer>
  );
};

export default Sidebar;
